/* eslint-disable no-useless-escape */
import { create } from "zustand";
import $api from "@/zapi-frontend/shared/api/axios";
import { waitForPendingMemoSave } from "@/zapi-frontend/shared/lib/pendingMemoSaves";
import { TGetProjectResponse, TChapter } from "../shared/types/projects";
import { analyzeChapterChanges } from "../utils/analyze-chapter-changes";
import { TSidebarChapter } from "../pages/ProjectDetailsPage/model/types/types";


type TCroppedMemoImage = {
  id: number;
  imageUrl: string;
  tlCoordinateX: number;
  tlCoordinateY: number;
  brCoordinateX: number;
  brCoordinateY: number;
};

export type ViewMode = "chapter" | "dashboard" | "projectOverview" | "instructions" | "projectFrame" | "projectConfig" | "vault" | "exports" | "valueChain" | "modelLedger" | "rqaSim" | "risks";

type TChapterStore = {
  // state
  projectId: string | null;
  projectChapters: TChapter[];
  activeChapter: TChapter | null;
  isOpenChapterSidebar: boolean;
  viewMode: ViewMode | undefined;
  sidebarChaptersData: TSidebarChapter[];
  activeSidebarChapterData: TSidebarChapter | null;
  activeChapterSubsection: string | null;
  activeSyncRequests: Map<string, Promise<string | null>>;

  // actions
  toggleChapterSidebar: (isOpen: boolean) => void;
  setViewMode: (mode: ViewMode | undefined) => void;
  setSidebarChaptersData: (chapters: TSidebarChapter[], activeChapterId: string | null) => void;
  setActiveChapterSubsection: (subsection: string | null) => void;
  setActiveChapter: (chapterId: string) => void;
  updateChapterContent: (chapterId: string, content: string, shouldCheckCurrent?: boolean) => void;
  updateChapterCurrent: (chapterId: string, current: boolean) => void;
  updateChapterCompleteness: (params: {
    chapterId: string;
    completeness: number;
    valid: number;
    total: number;
  }) => void;
  updateChapterCroppedMemoImage: (chapterId: string, imageData: TCroppedMemoImage) => Promise<void>;
  invalidateChapterCroppedImages: (chapterId: string) => Promise<void>;
  getCropCoordinatesForImage: (id: number) => TCroppedMemoImage | null;
  initializeProjectChapters: (project: TGetProjectResponse) => void;
  syncChapterContent: (chapterId: string, abortController?: AbortController) => Promise<string | null>;
  getChapterContent: (chapterId: string) => string | null;
  updateChapter: (params: { chapterId: number; value: Partial<TChapter> }) => void;

  updateChapterContentUpdatedAt: (chapterId: string, contentUpdatedAt: string) => void;
};

export const useChapterStore = create<TChapterStore>((set, get) => ({
  projectId: null,
  projectChapters: [],
  activeChapter: null,
  isOpenChapterSidebar: false,
  viewMode: undefined,
  sidebarChaptersData: [],
  activeSidebarChapterData: null,
  activeChapterSubsection: null,
  activeSyncRequests: new Map(),

  toggleChapterSidebar: (isOpen) => {
    set({ isOpenChapterSidebar: isOpen });
  },
  setViewMode: (mode) => {
    set({ viewMode: mode });
    if (mode === "dashboard" || mode === "projectOverview") {
      set({ activeChapter: null, activeSidebarChapterData: null });
    }
  },
  setSidebarChaptersData: (chapters, activeChapterId) => {
    const activeChapter = chapters.find((c) => c.id === activeChapterId) ?? null;
    set({ sidebarChaptersData: chapters, activeSidebarChapterData: activeChapter });
  },
  setActiveChapterSubsection: (subsection) => {
    set({ activeChapterSubsection: subsection });
  },
  setActiveChapter: (chapterId) => {
    set((state) => {
      const newChapter = state.projectChapters.find((c) => c.id === Number(chapterId));
      const newSidebarChapter = state.sidebarChaptersData.find((c) => c.id === chapterId);
      const activeSubsection = state.activeChapterSubsection;
      const isSubsectionValid = newSidebarChapter?.subsections.includes(activeSubsection || "") ?? null;

      if (newChapter)
        return {
          activeChapter: newChapter,
          activeSidebarChapterData: newSidebarChapter,
          activeChapterSubsection: isSubsectionValid ? activeSubsection : newSidebarChapter?.subsections[0],
          viewMode: "chapter" as ViewMode
        };

      return state;
    });
  },
  updateChapter: ({ chapterId, value }) => {
    set((state) => {
      const updatedChapters = state.projectChapters.map((chapter) =>
        chapter.id === chapterId ? { ...chapter, ...value } : chapter
      );
      return {
        projectChapters: updatedChapters,
        activeChapter:
          state.activeChapter?.id === chapterId ? { ...state.activeChapter, ...value } : state.activeChapter
      };
    });
  },

  updateChapterContent: (chapterId, content, shouldCheckCurrent = false) => {
    const { projectChapters, activeChapter } = get();
    const updatedChapter = projectChapters.find((c) => c.id === Number(chapterId));
    if (!updatedChapter) {
      return;
    }
    const oldContent = updatedChapter.activeMemo?.mdContent;
    const newContent = content;
    const isDifferent = analyzeChapterChanges(oldContent, newContent);
    const isCurrent = shouldCheckCurrent ? (isDifferent ? false : updatedChapter.isCurrent) : updatedChapter.isCurrent;
    const newChapter = {
      ...updatedChapter,
      activeMemo: updatedChapter.activeMemo
        ? {
            ...updatedChapter.activeMemo,
            mdContent: content,
            contentUpdatedAt: updatedChapter.activeMemo.contentUpdatedAt
          }
        : null,
      isCurrent
    };

    // If this is not the Executive Summary and content has changed, mark Executive Summary as stale
    const isExecutiveSummary = updatedChapter.projectTemplateChapter.name === "Executive Summary";
    let updatedChapters = projectChapters.map((chapter) => (chapter.id === Number(chapterId) ? newChapter : chapter));

    if (!isExecutiveSummary && isDifferent && oldContent !== newContent) {
      // Mark Executive Summary as not current when other chapters are updated
      updatedChapters = updatedChapters.map((chapter) =>
        chapter.projectTemplateChapter.name === "Executive Summary" ? { ...chapter, isCurrent: false } : chapter
      );
    }

    set(() => {
      return {
        projectChapters: updatedChapters
      };
    });
    // setActiveChapter would also switch to the chapter view, remounting whatever view is open.
    if (Number(chapterId) === activeChapter?.id) {
      set({ activeChapter: newChapter });
    }
  },
  updateChapterCurrent: (chapterId, current) => {
    set((state) => {
      return {
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId) ? { ...chapter, isCurrent: current } : chapter
        )
      };
    });
    if (Number(chapterId) === get().activeChapter?.id) {
      get().setActiveChapter(chapterId);
    }
  },
  updateChapterCompleteness: ({ chapterId, completeness, valid, total }) => {
    set((state) => {
      return {
        activeChapter:
          state.activeChapter?.id === Number(chapterId)
            ? {
                ...state.activeChapter,
                completenessRatio: completeness,
                validQuestionsCount: valid,
                totalQuestions: total
              }
            : state.activeChapter,
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId)
            ? { ...chapter, completenessRatio: completeness, validQuestionsCount: valid, totalQuestions: total }
            : chapter
        )
      };
    });
  },
  updateChapterCroppedMemoImage: async (chapterId, imageData) => {
    const { projectId, projectChapters } = get();
    if (!projectId) {
      throw new Error("Project ID not found");
    }

    const updatedChapter = projectChapters.find((c) => c.id === Number(chapterId));
    if (!updatedChapter) {
      return;
    }

    const oldImagesData = updatedChapter.activeMemo?.croppedMemoImages;
    const newImagesData = [...(oldImagesData || []).filter((image) => image.id !== imageData.id), imageData];

    const newChapter: TChapter = {
      ...updatedChapter,
      activeMemo: updatedChapter.activeMemo ? { ...updatedChapter.activeMemo, croppedMemoImages: newImagesData } : null
    };

    set((state) => {
      return {
        projectChapters: state.projectChapters.map((chapter) =>
          chapter.id === Number(chapterId) ? newChapter : chapter
        )
      };
    });

    // Update active chapter if it's the one that was modified
    if (Number(chapterId) === get().activeChapter?.id) {
      get().setActiveChapter(chapterId);
    }

    try {
      await $api.patch(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`, imageData);
    } catch (error) {
      set((state) => {
        return {
          projectChapters: state.projectChapters.map((chapter) =>
            chapter.id === Number(chapterId) ? updatedChapter : chapter
          )
        };
      });
      console.error("Failed to update cropped memo image:", error);
      throw error;
    }
  },
  invalidateChapterCroppedImages: async (chapterId) => {
    const { projectId, activeChapter } = get();
    if (!projectId) {
      return;
    }

    try {
      const response = await $api.get(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`);
      const croppedMemoImages = response.data as TCroppedMemoImage[];

      set((state) => {
        return {
          projectChapters: state.projectChapters.map((chapter) =>
            chapter.id === Number(chapterId)
              ? {
                  ...chapter,
                  activeMemo: chapter.activeMemo
                    ? {
                        ...chapter.activeMemo,
                        croppedMemoImages
                      }
                    : null
                }
              : chapter
          )
        };
      });

      // setActiveChapter would also switch to the chapter view, remounting whatever view is open.
      if (Number(chapterId) === activeChapter?.id) {
        set((state) => ({
          activeChapter: state.projectChapters.find((chapter) => chapter.id === Number(chapterId)) ?? state.activeChapter
        }));
      }
    } catch (error) {
      console.error("Failed to invalidate chapter cropped images:", error);
      throw error;
    }
  },
  getCropCoordinatesForImage: (id) => {
    const { activeChapter } = get();
    if (!activeChapter?.activeMemo?.croppedMemoImages) {
      return null;
    }

    const croppedMemoImage = activeChapter.activeMemo.croppedMemoImages.find((i) => i.id === id);
    return croppedMemoImage || null;
  },
  initializeProjectChapters: async (project: TGetProjectResponse) => {
    const { activeChapter, projectChapters: currentChapters } = get();
    // The project payload strips memo content (mdContent: "") to keep it small —
    // keep locally-known content instead of wiping it; syncChapterContent is the
    // authoritative refresh when a chapter is opened. Only preserve when the
    // incoming contentUpdatedAt is not newer than the local one: a genuinely
    // cleared chapter bumps contentUpdatedAt, and resurrecting deleted content
    // would be worse than showing empty until the open-time sync refills it.
    const chapters = project.projectChapters.map((incoming) => {
      const local = currentChapters.find((c) => c.id === incoming.id)?.activeMemo;
      if (!incoming.activeMemo || incoming.activeMemo.mdContent || !local?.mdContent) return incoming;
      const incomingUpdatedAt = incoming.activeMemo.contentUpdatedAt;
      if (!incomingUpdatedAt || !local.contentUpdatedAt) return incoming;
      if (new Date(incomingUpdatedAt) > new Date(local.contentUpdatedAt)) return incoming;
      return { ...incoming, activeMemo: { ...incoming.activeMemo, mdContent: local.mdContent } };
    });

    set({
      projectId: project.id.toString(),
      projectChapters: chapters
    });

    if (activeChapter) {
      const updatedActiveChapter = chapters.find((c) => c.id === activeChapter.id);
      if (updatedActiveChapter) {
        set({ activeChapter: updatedActiveChapter });
      }
    }
  },
  syncChapterContent: async (chapterId, abortController?: AbortController) => {
    const { projectId, projectChapters, activeSyncRequests } = get();
    if (!projectId || !chapterId) return null;
    const chapter = projectChapters.find((c) => c.id === Number(chapterId));

    if (!chapter || !chapter.activeMemo) return null;

    // Concurrent callers share one in-flight request; the fetch itself is never
    // aborted (a caller's abort only makes THAT caller ignore the result), so an
    // unmounted effect can't kill the request another mount is waiting on.
    const requestKey = `${projectId}-${chapterId}`;
    let request = activeSyncRequests.get(requestKey);
    if (!request) {
      request = (async (): Promise<string | null> => {
        try {
          await waitForPendingMemoSave(projectId, chapterId);
          const [chapterResponse, imagesResponse] = await Promise.all([
            $api.get(`/projects/${projectId}/chapters/${chapterId}`),
            $api.get(`/projects/${projectId}/chapters/${chapterId}/cropped-memo-image`)
          ]);

          const updatedChapter = (chapterResponse.data as { data: TChapter }).data;
          const croppedMemoImages = imagesResponse.data as TCroppedMemoImage[];

          if (!updatedChapter.activeMemo) return null;
          set((state) => {
            return {
              projectChapters: state.projectChapters.map((chapter) =>
                chapter.id === Number(chapterId)
                  ? {
                      ...chapter,
                      activeMemo: chapter.activeMemo
                        ? {
                            ...chapter.activeMemo,
                            croppedMemoImages,
                            contentUpdatedAt: updatedChapter.activeMemo?.contentUpdatedAt ?? new Date().toISOString(),
                            contentFormat: updatedChapter.activeMemo?.contentFormat ?? chapter.activeMemo.contentFormat
                          }
                        : null
                    }
                  : chapter
              )
            };
          });

          get().updateChapterContent(chapterId, updatedChapter.activeMemo.mdContent);
          return updatedChapter.activeMemo.mdContent;
        } catch (error: unknown) {
          if (!(error instanceof Error) || error.message !== "canceled") {
            console.error("Failed to sync chapter content:", error);
          }
          return null;
        } finally {
          const requests = new Map(get().activeSyncRequests);
          requests.delete(requestKey);
          set({ activeSyncRequests: requests });
        }
      })();
      set({ activeSyncRequests: new Map(get().activeSyncRequests).set(requestKey, request) });
    }

    const content = await request;
    return abortController?.signal.aborted ? null : content;
  },
  getChapterContent: (chapterId) => {
    const { projectChapters } = get();
    const chapter = projectChapters.find((c) => c.id === Number(chapterId));
    return chapter?.activeMemo?.mdContent || null;
  },

  updateChapterContentUpdatedAt: (chapterId, contentUpdatedAt) => {
    set((state) => {
      const updatedChapters = state.projectChapters.map((chapter) =>
        chapter.id === Number(chapterId) && chapter.activeMemo
          ? { ...chapter, activeMemo: { ...chapter.activeMemo, contentUpdatedAt } }
          : chapter
      );
      return {
        projectChapters: updatedChapters,
        activeChapter:
          state.activeChapter?.id === Number(chapterId) && state.activeChapter?.activeMemo
            ? { ...state.activeChapter, activeMemo: { ...state.activeChapter.activeMemo, contentUpdatedAt } }
            : state.activeChapter
      };
    });
  }
}));
